<template>
  <div v-show="products?.length || loading" class="pb-3 lg:pb-6">
    <div v-if="title" class="mx-4 flex items-center pb-2 pt-6 between lg:mx-6 lg:pb-4">
      <h2 v-style:c="titleColor" :class="titleStyle">
        <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
        <span v-else>{{ title }}</span>
      </h2>
      <div v-if="carouselRef?.carousel" class="flex <md:hidden">
        <base-button
          :aria-label="$t.previous"
          class="rounded-xs p-1 duration"
          :class="carouselRef.carousel.canScrollPrev ? '@hactive:bg-brand-2' : 'c-grey-60'"
          :disabled="!carouselRef.carousel.canScrollPrev"
          @click="carouselRef?.scrollPrev()"
        >
          <vf-icon dir="left" name="chevron" size="md" />
        </base-button>
        <base-button
          :aria-label="$t.next"
          class="rounded-xs p-1 duration"
          :class="carouselRef.carousel.canScrollNext ? '@hactive:bg-brand-2' : 'c-grey-60'"
          :disabled="!carouselRef.carousel.canScrollNext"
          @click="carouselRef?.scrollNext()"
        >
          <vf-icon dir="right" name="chevron" size="md" />
        </base-button>
      </div>
    </div>
    <vf-carousel-elevate
      ref="carouselRef"
      :class-container="brandClasses.carouselContainer"
      :class-controls="brandClasses.carouselControls"
      :show-arrows="$viewport.md && !title"
      :show-arrows-on-hover-only="$viewport.md && !title"
      size-controls="md"
      :slides-to-scroll="!$viewport.md ? 'auto' : (variant === '6-cards' ? 6 : 3)"
    >
      <template v-if="!products?.length">
        <div
          v-for="i in 6"
          :key="i"
          v-style:w="variantStyleMap[variant]"
          class="pr-1 children:skeleton"
        >
          <div style="aspect-ratio: 0.72" />
        </div>
      </template>
      <template v-else>
        <div
          v-for="(product, i) in products"
          :key="product.id"
          v-style:w="variantStyleMap[variant]"
          class="pr-1"
          @click="$emit('product-click', product, i + 1)"
        >
          <product-card-recommended
            :currency="currency"
            v-bind="product"
            :gtm-config="{ list: _list, category: _list }"
            :quickshop
          />
        </div>
      </template>
    </vf-carousel-elevate>
  </div>
</template>

<script lang="ts" setup>
import type { Responsive } from '#types/common'
import type { LayoutVariant } from '#types/components/cms/recommendation-carousel'
import type { ResponsiveColor } from '#types/content'
import { getPageInfo } from '#commerce/gtm/page'

const {
  quickshop = true,
  title,
  titleStyle = 'body-2',
  variant = '6-cards'
} = defineProps<{
  currency: string
  loading?: boolean
  products: any[]
  quickshop?: boolean
  title?: string
  titleColor?: ResponsiveColor
  titleHtml?: string
  titleStyle?: string
  variant?: LayoutVariant
}>()

defineEmits<{
  'product-click': [product: any, position: number]
}>()

const { brandClasses } = useAppConfig().components.cms.productRecommendations
const carouselRef = ref()
const route = useRoute()
const [pageCategory] = getPageInfo(route, {})

const variantStyleMap: Record<LayoutVariant, Responsive> = {
  '3-cards': { sm: '74.66%', md: '29.49%', lg: '29.58%' },
  '5-cards': { sm: '40%', md: '25%', lg: '20%' },
  '6-cards': { sm: '74.66%', md: '20%' },
  'search-hub': { sm: '44.44%', md: '30.03%', lg: '20%' }
}

const _list = getRecommendationListDescription({ list: `${pageCategory || 'Home'} | Product Recommendation Carousel`, title })
</script>
